/**
 * A little stage of its own: a PixiJS application with its own canvas, shape factory, and
 * viewport, plus a fixed-step clock, for the decorative scenes around the menus (the ninjas on
 * the join screen's seats, the race behind the title). Each is a separate renderer because the
 * game's canvas sits under the title backdrop and a texture belongs to the renderer that made
 * it. The scenes tick their own simulations at the game's rate from the rendered frame's clock,
 * catching up at most a few ticks per frame so a tab coming back does not spin.
 */

import { type RenderFeel, ShapeFactory, Viewport } from "@lache/render";
import { DT } from "@lache/sim";
import { Application } from "pixi.js";

/** The most simulation ticks one rendered frame catches up on (a tab coming back drops the rest). */
const MAX_TICKS_PER_FRAME = 8;

export interface MiniStageOptions {
  /** The element the canvas goes into. */
  parent: HTMLElement;
  className: string;
  /** Canvas size in CSS pixels, or `resizeTo` an element; the backing store follows the device. */
  width?: number;
  height?: number;
  resizeTo?: HTMLElement | Window;
  /** Background color, or transparent when omitted. */
  background?: number;
  /** Backing-store pixels per CSS pixel (the device's, capped at 2, when omitted); a backdrop can go lower. */
  resolution?: number;
}

export class MiniStage {
  readonly app = new Application();
  readonly viewport = new Viewport();
  /** Set once the renderer is up; nothing draws before. */
  ready = false;
  private factory: ShapeFactory | null = null;
  private lastMs = -1;
  private acc = 0;
  private fitW = -1;
  private fitH = -1;

  constructor(private readonly options: MiniStageOptions) {}

  get shapes(): ShapeFactory {
    if (!this.factory) throw new Error("stage not ready");
    return this.factory;
  }

  /** Bring the renderer up (asynchronous, like the game's) and put the canvas in the page. */
  async init(): Promise<void> {
    const o = this.options;
    await this.app.init({
      width: o.width ?? 256,
      height: o.height ?? 192,
      ...(o.resizeTo ? { resizeTo: o.resizeTo } : {}),
      ...(o.background !== undefined ? { background: o.background } : { backgroundAlpha: 0 }),
      antialias: true,
      resolution: o.resolution ?? Math.min(2, window.devicePixelRatio || 1),
      // The stylesheet sizes the canvas (it scales with its card), so Pixi leaves its style alone.
      autoDensity: false,
      preference: "webgl",
    });
    this.app.ticker.stop();
    this.app.canvas.className = o.className;
    o.parent.appendChild(this.app.canvas);
    this.factory = new ShapeFactory(this.app.renderer);
    this.app.stage.addChild(this.viewport.root);
    this.ready = true;
  }

  /** Canvas size in CSS pixels. */
  get width(): number {
    return this.app.screen.width;
  }

  get height(): number {
    return this.app.screen.height;
  }

  /** Size the viewport to the canvas whenever the canvas changed; `viewWidth` meters span its longer side. */
  fit(viewWidth: number, feel: RenderFeel): boolean {
    const target = this.options.resizeTo;
    if (target instanceof HTMLElement) {
      if (this.app.screen.width !== target.clientWidth || this.app.screen.height !== target.clientHeight) {
        this.app.resize();
      }
    }
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    if (w === this.fitW && h === this.fitH) return false;
    this.fitW = w;
    this.fitH = h;
    this.viewport.setRect(0, 0, w, h, viewWidth, feel, false);
    return true;
  }

  /**
   * Advance the fixed-step clock to `nowMs`: how many ticks to simulate this frame, the blend
   * between the last two ticks for drawing, and the frame's length in seconds.
   */
  clock(nowMs: number): { ticks: number; alpha: number; dt: number } {
    const dtMs = this.lastMs < 0 ? 1000 / 60 : Math.min(100, Math.max(0, nowMs - this.lastMs));
    this.lastMs = nowMs;
    this.acc += dtMs / 1000;
    let ticks = 0;
    while (this.acc >= DT && ticks < MAX_TICKS_PER_FRAME) {
      this.acc -= DT;
      ticks++;
    }
    if (ticks === MAX_TICKS_PER_FRAME) this.acc = 0;
    return { ticks, alpha: this.acc / DT, dt: dtMs / 1000 };
  }

  /** The clock starts over on the next frame (the scene was hidden for a while). */
  resetClock(): void {
    this.lastMs = -1;
    this.acc = 0;
  }

  render(): void {
    this.app.renderer.render(this.app.stage);
  }
}
